import {
  BookOpen,
  Bot,
  Calendar,
  CalendarXIcon,
  CircleDollarSignIcon,
  Home,
  Inbox,
} from "lucide-react"
import * as React from "react"
import { useTranslation } from "react-i18next"
import { NavLink } from "react-router-dom"
import HiveIcon from "@/assets/icons/bees/hive-icon.tsx"
import Logo from "@/assets/icons/logos/logo-clean.svg?react"
import FarmSidebarIcon from "@/assets/sidebar/farm-sidebar-icon.svg?react"
import NotificationSidebarIcon from "@/assets/sidebar/notifications-sidebar-icon.svg?react"
import { NavMain } from "@/components/app-sidebar/nav-main.tsx"
import { NavUser } from "@/components/app-sidebar/nav-user.tsx"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/shadcn/sidebar.tsx"
import {
  APIARIES_LIST_VIEW,
  HIVES_LIST_VIEW,
  QUEENS_LIST_VIEW,
} from "@/features/bees/routes/route-list.ts"
import {
  CROPS_LIST_VIEW,
  CROPS_PLANTING_LIST_VIEW,
} from "@/features/crops/routes/route-list.ts"
import { ANIMAL_EVENT_LIST_VIEW } from "@/features/events/animal-events/routes/route-list.ts"
import { APIARY_EVENT_LIST_VIEW } from "@/features/events/apiary-events/routes/route-list.ts"
import { BIRD_GROUP_EVENT_LIST_VIEW } from "@/features/events/birds-groups-events/routes/route-list.ts"
import { HIVE_EVENT_LIST_VIEW } from "@/features/events/hive-events/routes/route-list.ts"
import { FARM_VIEW } from "@/features/farm/routes/route-list.ts"
import { FINANCIAL_JOURNAL_VIEW } from "@/features/finance/routes/route-list.ts"
import { GESTATIONAL_CALCULATOR_LIST_VIEW } from "@/features/gestational-calendar/routes/route-list.ts"
import { HOME_SCREEN } from "@/features/home/routes/route-list.ts"
import { NOTES_LIST_VIEW } from "@/features/notes/routes/route-list.ts"
import { NOTIFICATIONS_LIST_VIEW } from "@/features/notifications/routes/route-list.ts"
import {
  PARCEL_EVENTS_VIEW,
  PARCELS_LIST_VIEW,
} from "@/features/parcels/routes/route-list.ts"
import { useGetMyFarmQuery } from "@/features/settings-account/api/account-api.ts"
import { TASKS_LIST_VIEW } from "@/features/tasks/routes/route-list.ts"

const FOCUS_RING =
  "outline-none focus-visible:ring-2 focus-visible:ring-[var(--brown)] focus-visible:ring-offset-1"

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { data: myFarm } = useGetMyFarmQuery()
  const { t } = useTranslation()

  const quickAccessItems = [
    {
      title: t("beekeeping"),
      icon: HiveIcon,
      items: [
        { title: t("apiaries"), url: APIARIES_LIST_VIEW },
        { title: t("hives"), url: HIVES_LIST_VIEW },
        { title: t("allQueens"), url: QUEENS_LIST_VIEW },
      ],
    },
    {
      title: t("events"),
      icon: Bot,
      items: [
        { title: t("animals"), url: ANIMAL_EVENT_LIST_VIEW },
        { title: t("hives"), url: HIVE_EVENT_LIST_VIEW },
        { title: t("apiaries"), url: APIARY_EVENT_LIST_VIEW },
        { title: t("birdsGroups"), url: BIRD_GROUP_EVENT_LIST_VIEW },
        { title: t("parcels"), url: PARCEL_EVENTS_VIEW },
      ],
    },
    {
      title: t("Vegetal"),
      icon: BookOpen,
      items: [
        { title: t("plantTypes"), url: CROPS_LIST_VIEW },
        { title: t("cropPlantings"), url: CROPS_PLANTING_LIST_VIEW },
      ],
    },
  ]

  const menuItems = [
    { title: t("homeTab"), url: HOME_SCREEN, icon: Home },
    { title: t("myFarm"), url: FARM_VIEW, icon: FarmSidebarIcon },
    { title: t("tasks"), url: TASKS_LIST_VIEW, icon: CalendarXIcon },
    { title: t("notes"), url: NOTES_LIST_VIEW, icon: Calendar },
    {
      title: t("financialJournal"),
      url: FINANCIAL_JOURNAL_VIEW,
      icon: CircleDollarSignIcon,
    },
    {
      title: t("notifications"),
      url: NOTIFICATIONS_LIST_VIEW,
      icon: NotificationSidebarIcon,
    },
    { title: t("MyParcels"), url: PARCELS_LIST_VIEW, icon: Inbox },
    {
      title: t("gestationalCalendar"),
      url: GESTATIONAL_CALCULATOR_LIST_VIEW,
      icon: Calendar,
    },
  ]

  return (
    <Sidebar collapsible="none" {...props}>
      <SidebarHeader>
        <div className="flex flex-col items-center">
          <Logo className="h-14 w-14" />
          {myFarm && (
            <p className="text-body-xs font-medium text-neutral-600">
              {myFarm.name}
            </p>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>{t("menu")}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <NavLink to={item.url} className="w-full">
                    {({ isActive }) => (
                      <SidebarMenuButton
                        className={`h-12 rounded-sm transition-colors ${FOCUS_RING} [@media(hover:hover)]:hover:text-brown-500 ${
                          isActive ? "bg-brown-50 text-brown-500" : ""
                        }`}
                      >
                        <item.icon />
                        <span className="font-medium">{item.title}</span>
                      </SidebarMenuButton>
                    )}
                  </NavLink>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <NavMain items={quickAccessItems} title={t("quickAccess")} />
      </SidebarContent>

      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
